import React, { useState } from 'react';
import { Button, Row, Col, Form, message } from 'antd';
import { EditStaffingRequestForm, FormValuesType } from '@jobs/components';
import { useQuery, useMutation } from '@common/hooks';
import { Drawer, ErrorHandler, Modal } from '@common/components';
import {
  STAFFING_REQUEST_QUERY,
  UPDATE_STAFFING_REQUEST_AND_APPROVE_STEP_MUTATION,
} from '@jobs/graphql';
import { addErrorsToForm } from 'works/legacy/lib/form-helpers';
import { hasFormAnyErrors } from '@common/services';

export function EditStaffingRequestOnApprovalDrawer({
  open,
  onClose,
  staffingRequestId,
}: {
  open: boolean;
  onClose: () => void;
  staffingRequestId?: string;
}) {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState<1 | 3>(1);
  const [hasErrors, setHasErrors] = useState(false);

  const {
    error,
    data,
    loading: queryLoading,
  } = useQuery(STAFFING_REQUEST_QUERY, {
    skip: !staffingRequestId,
    variables: {
      id: staffingRequestId!,
    },
  });

  const [updateStaffingRequestAndApproveStep, { loading: loadingMutation }] =
    useMutation(UPDATE_STAFFING_REQUEST_AND_APPROVE_STEP_MUTATION, {
      onCompleted: (mutationData) => {
        const result = mutationData?.updateStaffingRequestAndApproveStep;
        if (result?.staffingRequest) {
          message.success('Staffing Request has been updated');
          onFormClose();
        }

        if (result?.errors) {
          addErrorsToForm(form, result?.errors);
        }
      },
      onError: () => message.error('Error occurred, try again later'),
      refetchQueries: [
        'StaffingRequestApplicationData',
        'StaffingRequestPageQuery',
      ],
    });

  const onSubmit = (values: FormValuesType) => {
    if (!staffingRequestId) {
      return;
    }

    updateStaffingRequestAndApproveStep({
      variables: {
        staffingRequestId: staffingRequestId,
        input: {
          openPositions: values.openPositions,
          approvalNotes: values.commentContent,
        },
      },
    });
  };

  const onFormClose = () => {
    setCurrentStep(1);
    form.resetFields();
    onClose();
  };

  if (error) {
    return (
      <ErrorHandler
        error={error}
        messageType="contextual"
        log="EditStaffingRequestDrawer"
      />
    );
  }

  return (
    <>
      <Drawer
        title="Edit Staffing Request"
        open={open}
        onClose={onFormClose}
        loading={queryLoading || !data?.staffingRequest}
        footer={
          <Row gutter={[16, 0]}>
            <Col>
              <Button
                key="cancel"
                size="large"
                onClick={() => {
                  if (currentStep === 1) {
                    onClose();
                  } else {
                    setCurrentStep(1);
                  }
                }}
              >
                {currentStep === 1 ? 'Cancel' : 'Back'}
              </Button>
            </Col>
            <Col>
              <Button
                key="action"
                type="primary"
                size="large"
                onClick={() => {
                  if (currentStep === 1) {
                    setCurrentStep(3);
                  } else {
                    confirmChangesModal(form.submit);
                  }
                }}
                loading={loadingMutation}
                disabled={hasErrors || !staffingRequestId}
              >
                Continue
              </Button>
            </Col>
          </Row>
        }
      >
        {data?.staffingRequest && (
          <EditStaffingRequestForm
            form={form}
            onSubmit={onSubmit}
            enableOnlyOpenPositionsField
            isApprovalNotesRequired={false}
            certifications={data.certificationList}
            clinicalSpecialties={data.clinicalSpecialties}
            technologies={data.technologies}
            staffingRequest={data.staffingRequest}
            partOneVisible={currentStep === 1}
            partTwoVisible={false}
            partThreeVisible={currentStep === 3}
            onValuesChange={() => hasFormAnyErrors(form, setHasErrors)}
          />
        )}
      </Drawer>
    </>
  );
}

function confirmChangesModal(onConfirm: () => void) {
  Modal.confirm({
    title: 'Confirm changes on this Staffing Request?',
    content:
      'Once updated, a notification will be sent to the requestor and previous approvers.',
    onOk: onConfirm,
    okText: 'Confirm & Approve',
  });
}
